import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AdminSidebar, StatCard, SkeletonCard, PageHeader } from "@/components/AdminLayout";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from "@/utils/api";

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('90');
  const [workers, setWorkers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'completion_rate', direction: 'desc' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, coursesRes, progressRes] = await Promise.all([
        api.get("/users"),
        api.get("/courses"),
        api.get("/progress/all").catch(() => ({ data: [] }))
      ]);
      
      setWorkers(usersRes.data.filter(u => u.role === 'worker'));
      setCourses(coursesRes.data);
      setProgress(progressRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  // Generate trend data for the last 90 days
  const generateTrendData = () => {
    const days = parseInt(dateRange);
    const data = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      // Simulated realistic data with some variance
      const baseCompletions = Math.floor(Math.random() * 3) + 1;
      const baseQuizPasses = Math.floor(baseCompletions * 0.8);
      const baseCertificates = Math.floor(baseCompletions * 0.7);
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        completions: baseCompletions + Math.floor(i / 30), // Slight upward trend
        quizPasses: baseQuizPasses,
        certificates: baseCertificates
      });
    }
    return data;
  };

  // Department performance data
  const getDeptPerformance = () => {
    const departments = [...new Set(workers.map(w => w.department).filter(Boolean))];
    return departments.map(dept => {
      const deptWorkers = workers.filter(w => w.department === dept);
      const deptProgress = progress.filter(p => deptWorkers.find(w => w.id === p.worker_id));
      const completed = deptProgress.filter(p => p.completed).length;
      const total = deptProgress.length || 1;
      return {
        name: dept,
        completion: Math.round((completed / total) * 100),
        workers: deptWorkers.length
      };
    }).sort((a, b) => b.completion - a.completion);
  };

  // Course effectiveness data
  const getCourseEffectiveness = () => {
    return courses.map(course => {
      const courseProgress = progress.filter(p => p.course_id === course.id);
      const enrolled = courseProgress.length;
      const completed = courseProgress.filter(p => p.completed).length;
      const avgScore = courseProgress.length > 0
        ? Math.round(courseProgress.reduce((sum, p) => sum + (p.final_quiz_score || 0), 0) / courseProgress.filter(p => p.final_quiz_score).length) || 0
        : 0;
      const passRate = enrolled > 0 ? Math.round((completed / enrolled) * 100) : 0;
      
      return {
        id: course.id,
        name: course.title,
        enrolled,
        completed,
        avgScore,
        passRate,
        avgDays: Math.floor(Math.random() * 7) + 2, // Mock
        certificates: completed
      };
    });
  };

  // Workers at risk
  const getWorkersAtRisk = () => {
    return workers.filter(w => {
      const workerProgress = progress.filter(p => p.worker_id === w.id);
      if (workerProgress.length === 0) return true;
      const avgProgress = workerProgress.reduce((sum, p) => sum + (p.video_progress || 0), 0) / workerProgress.length;
      return avgProgress < 50 && !workerProgress.some(p => p.completed);
    }).map(w => {
      const workerProgress = progress.filter(p => p.worker_id === w.id);
      const courseInProgress = workerProgress.length > 0 
        ? courses.find(c => c.id === workerProgress[0]?.course_id)?.title || 'Unknown'
        : 'Not Started';
      const currentProgress = workerProgress.length > 0 
        ? Math.round(workerProgress.reduce((sum, p) => sum + (p.video_progress || 0), 0) / workerProgress.length)
        : 0;
      
      return {
        ...w,
        courseName: courseInProgress,
        currentProgress,
        daysSinceLogin: Math.floor(Math.random() * 14) + 1 // Mock
      };
    });
  };

  // Compliance data
  const getComplianceData = () => {
    return workers.flatMap(w => {
      const workerProgress = progress.filter(p => p.worker_id === w.id);
      if (workerProgress.length === 0) {
        return [{
          workerId: w.id,
          workerName: w.full_name,
          department: w.department,
          courseName: 'No courses assigned',
          status: 'not_assigned',
          completionDate: null,
          expiryDate: null,
          daysRemaining: null
        }];
      }
      
      return workerProgress.map(p => {
        const course = courses.find(c => c.id === p.course_id);
        const completionDate = p.completed ? new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000) : null;
        const expiryDate = completionDate ? new Date(completionDate.getTime() + 365 * 24 * 60 * 60 * 1000) : null;
        const daysRemaining = expiryDate ? Math.floor((expiryDate - new Date()) / (24 * 60 * 60 * 1000)) : null;
        
        let status = 'in_progress';
        if (p.completed) {
          if (daysRemaining && daysRemaining < 0) status = 'expired';
          else if (daysRemaining && daysRemaining < 30) status = 'expiring_soon';
          else status = 'current';
        }
        
        return {
          workerId: w.id,
          workerName: w.full_name,
          department: w.department,
          courseName: course?.title || 'Unknown Course',
          status,
          completionDate,
          expiryDate,
          daysRemaining
        };
      });
    });
  };

  // Calculated stats
  const totalCompletions = progress.filter(p => p.completed).length;
  const avgQuizScore = progress.length > 0
    ? Math.round(progress.filter(p => p.final_quiz_score).reduce((sum, p) => sum + p.final_quiz_score, 0) / progress.filter(p => p.final_quiz_score).length) || 0
    : 0;
  const overallCompletionRate = progress.length > 0 
    ? Math.round((totalCompletions / progress.length) * 100) 
    : 0;
  const activeLearnersThisMonth = workers.filter(w => {
    const wp = progress.filter(p => p.worker_id === w.id);
    return wp.some(p => p.video_progress > 0);
  }).length;

  // Sorting for course table
  const sortedCourses = [...getCourseEffectiveness()].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc'
    });
  };

  const handleExport = (type) => {
    toast.info(`${type} export coming soon!`, {
      description: "This feature is under development."
    });
  };

  if (loading) {
    return (
      <div className="flex">
        <AdminSidebar active="analytics" />
        <main className="admin-content">
          <div className="mb-6">
            <div className="h-8 bg-slate-200 rounded w-48 mb-2 animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex">
      <AdminSidebar active="analytics" />
      <main className="admin-content">
        <PageHeader title="Analytics & Reports" subtitle="Training performance insights">
          <Button
            onClick={() => handleExport('PDF')}
            variant="outline"
            className="border-[#2e7fd4] text-[#2e7fd4] hover:bg-[#2e7fd4] hover:text-white"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export PDF
          </Button>
          <Button
            onClick={() => handleExport('Excel')}
            className="bg-[#0a2240] hover:bg-[#0f325c] text-white"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export Excel
          </Button>
        </PageHeader>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'compliance', label: 'Compliance Tracker' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#2e7fd4] text-[#2e7fd4]'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                icon={<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                iconBg="bg-green-100"
                value={totalCompletions}
                label="Total Completions"
              />
              <StatCard
                icon={<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
                iconBg="bg-blue-100"
                value={`${avgQuizScore}%`}
                label="Avg Quiz Score"
              />
              <StatCard
                icon={<svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
                iconBg="bg-purple-100"
                value={`${overallCompletionRate}%`}
                label="Completion Rate"
              />
              <StatCard
                icon={<svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
                iconBg="bg-teal-100"
                value={activeLearnersThisMonth}
                label="Active This Month"
              />
            </div>

            {/* Completion Trend Chart */}
            <div className="chart-container mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="chart-title">Completion Trend</h3>
                <div className="flex gap-2">
                  {['30', '60', '90'].map(days => (
                    <button
                      key={days}
                      onClick={() => setDateRange(days)}
                      className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                        dateRange === days
                          ? 'bg-[#0a2240] text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {days}d
                    </button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={generateTrendData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#64748b" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#64748b" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="completions" name="Completions" stroke="#0e9f8e" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="quizPasses" name="Quiz Passes" stroke="#2e7fd4" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="certificates" name="Certificates" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Two Column: Department Performance & Workers at Risk */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Department Performance Bar Chart */}
              <div className="chart-container">
                <h3 className="chart-title">Department Performance</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={getDeptPerformance()} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} stroke="#64748b" />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} stroke="#64748b" width={100} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [`${value}%`, 'Completion']}
                    />
                    <Bar 
                      dataKey="completion" 
                      fill="#2e7fd4" 
                      radius={[0, 4, 4, 0]}
                      label={{ position: 'right', fontSize: 12, fill: '#64748b' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Workers at Risk */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Workers at Risk</h3>
                  <span className="badge badge-danger">{getWorkersAtRisk().length}</span>
                </div>
                <div className="space-y-3 max-h-[240px] overflow-y-auto">
                  {getWorkersAtRisk().slice(0, 6).map((worker) => (
                    <div key={worker.id} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{worker.full_name}</p>
                        <p className="text-xs text-slate-500">{worker.employee_id} • {worker.department || 'No dept'}</p>
                      </div>
                      <div className="text-right">
                        <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-red-500 rounded-full" style={{ width: `${worker.currentProgress}%` }}></div>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{worker.currentProgress}%</p>
                      </div>
                      <span className="text-xs text-red-600 font-medium whitespace-nowrap">{worker.daysSinceLogin}d ago</span>
                    </div>
                  ))}
                  {getWorkersAtRisk().length === 0 && (
                    <div className="text-center py-8">
                      <svg className="w-12 h-12 mx-auto text-green-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-green-700 font-medium">All workers on track!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Course Effectiveness Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">Course Effectiveness</h3>
              </div>
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')} className={sortConfig.key === 'name' ? 'sorted' : ''}>
                        Course Name <span className="sort-icon">{sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th onClick={() => handleSort('enrolled')} className={sortConfig.key === 'enrolled' ? 'sorted' : ''}>
                        Enrolled <span className="sort-icon">{sortConfig.key === 'enrolled' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th onClick={() => handleSort('completed')} className={sortConfig.key === 'completed' ? 'sorted' : ''}>
                        Completed <span className="sort-icon">{sortConfig.key === 'completed' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th onClick={() => handleSort('avgScore')} className={sortConfig.key === 'avgScore' ? 'sorted' : ''}>
                        Avg Score <span className="sort-icon">{sortConfig.key === 'avgScore' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th onClick={() => handleSort('passRate')} className={sortConfig.key === 'passRate' ? 'sorted' : ''}>
                        Pass Rate <span className="sort-icon">{sortConfig.key === 'passRate' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th>Avg Days</th>
                      <th>Certificates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCourses.map((course) => (
                      <tr key={course.id}>
                        <td className="font-medium">{course.name}</td>
                        <td>{course.enrolled}</td>
                        <td>{course.completed}</td>
                        <td>
                          <span className={`font-medium ${course.avgScore >= 70 ? 'text-green-600' : course.avgScore >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                            {course.avgScore}%
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${course.passRate >= 70 ? 'bg-green-500' : course.passRate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ width: `${course.passRate}%` }}
                              ></div>
                            </div>
                            <span className="text-sm">{course.passRate}%</span>
                          </div>
                        </td>
                        <td>{course.avgDays}d</td>
                        <td>{course.certificates}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === 'compliance' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Compliance Tracker</h3>
              <div className="flex gap-2">
                <span className="badge badge-success">Current</span>
                <span className="badge badge-warning">Expiring Soon</span>
                <span className="badge badge-danger">Expired/Overdue</span>
              </div>
            </div>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th>Department</th>
                    <th>Course</th>
                    <th>Status</th>
                    <th>Completion Date</th>
                    <th>Expiry Date</th>
                    <th>Days Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {getComplianceData().map((item, idx) => (
                    <tr key={idx} className={item.status === 'expired' ? 'bg-red-50' : item.status === 'expiring_soon' ? 'bg-amber-50' : ''}>
                      <td className="font-medium">{item.workerName}</td>
                      <td>{item.department || '-'}</td>
                      <td>{item.courseName}</td>
                      <td>
                        {item.status === 'current' && <span className="badge badge-success">Current</span>}
                        {item.status === 'expiring_soon' && <span className="badge badge-warning">Expiring Soon</span>}
                        {item.status === 'expired' && <span className="badge badge-danger">Expired</span>}
                        {item.status === 'in_progress' && <span className="badge badge-info">In Progress</span>}
                        {item.status === 'not_assigned' && <span className="badge badge-neutral">Not Assigned</span>}
                      </td>
                      <td>{item.completionDate ? item.completionDate.toLocaleDateString() : '-'}</td>
                      <td>{item.expiryDate ? item.expiryDate.toLocaleDateString() : '-'}</td>
                      <td>
                        {item.daysRemaining !== null ? (
                          <span className={`font-medium ${
                            item.daysRemaining < 0 ? 'text-red-600' : 
                            item.daysRemaining < 30 ? 'text-amber-600' : 'text-green-600'
                          }`}>
                            {item.daysRemaining < 0 ? `${Math.abs(item.daysRemaining)}d overdue` : `${item.daysRemaining}d`}
                          </span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
